import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { LuClipboardPaste } from "react-icons/lu";
import { appendLossNodeToPipeline, editLossSubtractorNode, reconfigureNode } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './LossSubtractorConfigModal.css';

// Set the app element for accessibility
try {
  Modal.setAppElement('#root');
} catch (error) {
  console.warn('Could not set app element for Modal:', error);
  // This prevents the app from crashing if the root element isn't available
}

const UNSPECIFIED_COLUMN = 'Column name not specified';

const normalizeOptionalColumn = (value, sentinelLabel = null) => {
  if (value === undefined || value === null) {
    return '';
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return '';
  }
  if (sentinelLabel && trimmed.toLowerCase() === sentinelLabel.toLowerCase()) {
    return '';
  }
  return String(value);
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  }
};

const LossSubtractorConfigModal = ({ isOpen, onClose, onConfigured, initialData = {} }) => {
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [transmissionLossPerc, setTransmissionLossPerc] = useState(initialData.transmissionLossPerc || initialData.lossPerc || '0');
  const [wheelingLossPerc, setWheelingLossPerc] = useState(initialData.wheelingLossPerc || '0');
  const [colName, setColName] = useState(normalizeOptionalColumn(initialData.colName, UNSPECIFIED_COLUMN));
  const { session } = usePipeline(); // Use the context to get the session
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReconfiguring, setIsReconfiguring] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Keep track of whether the modal has been opened
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Update state only when the modal is opened or initialData changes before user interaction
  useEffect(() => {
    if (isOpen) {
      // Only initialize values if this is the first time opening or if the modal was closed and reopened
      if (!hasInitialized) {
        setNodeName(initialData.name || '');
        setTransmissionLossPerc(initialData.transmissionLossPerc || initialData.lossPerc || '0');
        setWheelingLossPerc(initialData.wheelingLossPerc || '0');
        setColName(normalizeOptionalColumn(initialData.colName, UNSPECIFIED_COLUMN));
        
        // Determine if we're reconfiguring or editing
        setIsReconfiguring(initialData.isReconfiguring || false);
        setIsEditing(initialData.isEditing || false);
        
        setHasInitialized(true);
        
        console.log('Modal opened with data:', {
          initialData,
          isReconfiguring: initialData.isReconfiguring || false,
          isEditing: initialData.isEditing || false
        });
      }
    } else {
      // Reset the initialization flag when the modal is closed
      setHasInitialized(false);
    }
  }, [isOpen, initialData, hasInitialized]);
  
  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);
  
  // Function to paste from clipboard
  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setColName(text);
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        // No need for alert as the button will be disabled
        return;
      }

      // Validate loss percentages
      if (transmissionLossPerc === '' || isNaN(transmissionLossPerc)) {
        setSubmitError({ title: 'Invalid transmission loss', message: 'Please enter a valid transmission loss %.' });
        return;
      }
      if (wheelingLossPerc === '' || isNaN(wheelingLossPerc)) {
        setSubmitError({ title: 'Invalid wheeling loss', message: 'Please enter a valid wheeling loss %.' });
        return;
      }

      // Column name optional now
      
      // Get the input node name and position from initialData
      const inputNodeName = initialData.inputNodeName;
      const position = initialData.position || { x: 0, y: 0 };
      const normalizedColName = normalizeOptionalColumn(colName, UNSPECIFIED_COLUMN);
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      // Make the API call if we have a valid pipeline name and input node
      if (session.pipeline_name !== "Unsaved_Pipeline" && inputNodeName) {
        try {
          // Determine which API to call based on whether we're reconfiguring, editing, or creating
          if (isReconfiguring) {
            // If reconfiguring, call the reconfigure API
            console.log(`Calling API to reconfigure loss subtractor node: ${nodeName} in pipeline: ${session.pipeline_name}`);
            console.log(`New input node: ${inputNodeName}`);
            
            await reconfigureNode(
              session.pipeline_name,
              nodeName,
              inputNodeName
            );
            
            console.log(`Successfully reconfigured loss subtractor node ${nodeName} with new input ${inputNodeName}`);
          } else if (isEditing && initialData.name) {
            // If editing, call the edit API
            console.log(`Calling API to edit loss subtractor node: ${initialData.name} in pipeline: ${session.pipeline_name}`);
            console.log(`New values - Transmission Loss: ${transmissionLossPerc}, Wheeling Loss: ${wheelingLossPerc}, Column: ${normalizedColName}`);
            
            await editLossSubtractorNode(
              session.pipeline_name,
              initialData.name,  // Use the original name
              transmissionLossPerc,
              wheelingLossPerc,
              normalizedColName || null
            );
            
            console.log(`Successfully edited loss subtractor node ${initialData.name}`);
          } else {
            // If creating a new node, call the append API
            console.log(`Calling API to append loss subtractor node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Input node: ${inputNodeName}, Transmission Loss: ${transmissionLossPerc}, Wheeling Loss: ${wheelingLossPerc}, Column: ${normalizedColName}, Position: (${position.x}, ${position.y})`);
            
            await appendLossNodeToPipeline(
              session.pipeline_name,
              nodeName,
              transmissionLossPerc,
              wheelingLossPerc,
              normalizedColName || "",
              inputNodeName,
              position.x,
              position.y
            );
            
            console.log(`Successfully added loss subtractor node ${nodeName} to backend`);
          }
        } catch (apiError) {
          console.error('Error calling loss subtractor node API:', apiError);
          const op = isReconfiguring ? OP.node.reconfigure : isEditing ? OP.node.editLossSubtractor : OP.node.appendLossSubtractor;
          const info = notifyError(apiError, { operation: op, showToast: false });
          setSubmitError(info);
          // Don't continue with UI updates if API call fails
          setIsSubmitting(false);
          return;
        }
      }
      
      // Call the callback to update the UI
      onConfigured({
        name: isEditing ? initialData.name : nodeName, // Keep original name in edit mode
        transmissionLossPerc: transmissionLossPerc,
        wheelingLossPerc: wheelingLossPerc,
        lossPerc: (parseFloat(transmissionLossPerc || 0) + parseFloat(wheelingLossPerc || 0)).toString(),
        colName: normalizedColName
      });
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring loss subtractor node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Loss Subtractor Configuration"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false} // Prevents warning about appElement
    >
      <div className="loss-modal">
        <button onClick={onClose} className="loss-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="loss-modal-title">
          {isEditing 
            ? 'Edit Loss Subtractor Node' 
            : 'Configure Loss Subtractor Node'}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="loss-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <div className="loss-modal-field">
          <label className="loss-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={nodeName}
              className="loss-modal-input"
              disabled={true}
              title="Node name cannot be changed in edit mode"
            />
          ) : (
            <input
              type="text"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              onBlur={() => {
                // Validate again on blur to ensure we show errors when user leaves the field
                const validation = validateNodeName(nodeName);
                setNodeNameError(validation.isValid ? '' : validation.errorMessage);
              }}
              className={`loss-modal-input ${nodeNameError ? 'error' : ''}`}
              placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
              aria-invalid={!!nodeNameError}
              aria-describedby={nodeNameError ? "node-name-error" : undefined}
            />
          )}
          {!isEditing && nodeNameError && (
            <div className="loss-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!isEditing && !nodeNameError && (
            <div className="loss-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
        </div>
        
        <div className="loss-modal-field">
          <label className="loss-modal-label">
            Transmission Loss Percentage:
          </label>
          <input
            type="number"
            value={transmissionLossPerc}
            onChange={(e) => setTransmissionLossPerc(e.target.value)}
            className="loss-modal-input"
            placeholder="Enter transmission loss %"
            step="0.1"
            min="0"
            max="100"
          />
        </div>

        <div className="loss-modal-field">
          <label className="loss-modal-label">
            Wheeling Loss Percentage:
          </label>
          <input
            type="number"
            value={wheelingLossPerc}
            onChange={(e) => setWheelingLossPerc(e.target.value)}
            className="loss-modal-input"
            placeholder="Enter wheeling loss %"
            step="0.1"
            min="0"
            max="100"
          />
        </div>

        <div className="loss-modal-field">
          <label className="loss-modal-label">
            Column Name:
          </label>
          <div className="input-with-icon">
            <input
              type="text"
              value={colName}
              onChange={(e) => setColName(e.target.value)}
              className="loss-modal-input"
              placeholder="Leave blank to auto-select last output column"
              title="Optional: leave blank to auto-select the last output column from the previous node"
            />
            <button 
              type="button"
              onClick={pasteFromClipboard}
              className="paste-button"
              title="Paste column name from clipboard"
            >
              <LuClipboardPaste size={18} />
            </button>
          </div>
        </div>
        
        <div className="loss-modal-button-container">
          <button 
            className="loss-modal-configure-button"
            onClick={handleConfigure}
            disabled={
              isSubmitting || 
              (!isEditing && (!!nodeNameError || !nodeName)) || 
              transmissionLossPerc === '' || isNaN(transmissionLossPerc) ||
              wheelingLossPerc === '' || isNaN(wheelingLossPerc)
            }
            title={
              nodeNameError ? nodeNameError : 
              (!nodeName && !isEditing) ? 'Node name is required' : 
              (transmissionLossPerc === '' || isNaN(transmissionLossPerc) || wheelingLossPerc === '' || isNaN(wheelingLossPerc)) ? 'Valid transmission and wheeling loss percentages are required' : ''
            }
          >
            {isSubmitting 
              ? (isEditing ? 'Saving...' : 'Configuring...') 
              : (isEditing ? 'Save Changes' : 'Configure')
            }
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LossSubtractorConfigModal;